import React, { useEffect, useState } from "react";
import { Button, Card, Empty, Segmented, Space, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { formatDay, formatEuro } from "../workReports/format";
import { listGuarantees } from "./api";
import GuaranteeModal from "./GuaranteeModal";
import {
  GUARANTEE_COLORS,
  directionLabels,
  guaranteeStatusLabels,
  guaranteeTypeLabels
} from "./labels";

/** Bürgschaften given and received; `openId` opens one directly (from a deadline link). */
const GuaranteeList = ({ meta, project, openId, onMetaChanged }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const isMobile = useIsMobile();
  const [status, setStatus] = useState("open");
  const [direction, setDirection] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(openId ? { id: openId } : null);
  const labels = guaranteeStatusLabels(t);
  const types = guaranteeTypeLabels(t);
  const directions = directionLabels(t);

  const load = () => {
    setLoading(true);
    listGuarantees({
      status: status === "all" ? undefined : status,
      direction: direction === "all" ? undefined : direction,
      project
    })
      .then(res => setData(res.data.data))
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  };

  useEffect(load, [status, direction, project]);
  useEffect(() => {
    if (openId) setModal({ id: openId });
  }, [openId]);

  const columns = [
    {
      title: t("Guarantee bond"),
      key: "number",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Button type="link" className="wty-link" onClick={() => setModal({ id: item._id })}>
            {types[item.type]} {item.bondNumber || item.number}
          </Button>
          <div className="wty-small">
            {[item.issuer?.name, directions[item.direction]].filter(Boolean).join(" · ")}
          </div>
        </div>
      )
    },
    {
      title: t("For"),
      key: "party",
      mobile: "subtitle",
      render: (_, item) =>
        [
          item.direction === "received"
            ? item.supplierInfo?.name || item.principal
            : item.clientInfo?.name || item.beneficiary,
          item.projectInfo?.name
        ]
          .filter(Boolean)
          .join(" · ") || "—"
    },
    {
      title: t("Amount"),
      key: "amount",
      align: "right",
      width: 130,
      render: (_, item) => formatEuro(item.amount, lng)
    },
    {
      title: t("Due back"),
      key: "returnDue",
      width: 120,
      render: (_, item) => formatDay(item.returnDue) || "—"
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      width: 170,
      render: (_, item) => (
        <Space size={[4, 4]} wrap>
          <Tag color={GUARANTEE_COLORS[item.status]}>{labels[item.status]}</Tag>
          {item.returnOverdue && <Tag color="error">{t("Due back")}</Tag>}
          {item.hasLetter && item.status === "active" && <Tag>{t("Letter ready")}</Tag>}
        </Space>
      )
    }
  ];

  const table = (
    <ResponsiveTable
      rowKey="_id"
      loading={loading}
      columns={columns}
      dataSource={data}
      mobileAvatar={false}
      onRow={record => ({ onDoubleClick: () => setModal({ id: record._id }) })}
      pagination={data.length > 50 ? { pageSize: 50 } : false}
      scroll={{ x: 760 }}
    />
  );

  return (
    <div>
      <PageToolbar
        filters={
          <Space direction="vertical" className="wty-full">
            <Segmented
              value={status}
              onChange={setStatus}
              options={[
                { value: "open", label: t("Open") },
                { value: "returned", label: labels.returned },
                { value: "drawn", label: labels.drawn },
                { value: "all", label: t("All") }
              ]}
            />
            <Segmented
              value={direction}
              onChange={setDirection}
              options={[
                { value: "all", label: t("All") },
                { value: "given", label: directions.given },
                { value: "received", label: directions.received }
              ]}
            />
          </Space>
        }
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ id: null })}>
              {t("New guarantee bond")}
            </Button>
          )
        }
      />
      {!loading && !data.length ? (
        <Card>
          <Empty
            description={
              <Typography.Paragraph type="secondary" className="wty-empty-text">
                {t(
                  "Track bonds you gave to clients and bonds you hold from subcontractors. When one is due back, a deadline and a draft return letter are prepared."
                )}
              </Typography.Paragraph>
            }
          />
        </Card>
      ) : isMobile ? (
        table
      ) : (
        <Card styles={{ body: { padding: 0 } }}>{table}</Card>
      )}
      <GuaranteeModal
        open={Boolean(modal)}
        id={modal?.id}
        meta={meta}
        project={project}
        onClose={() => setModal(null)}
        onChanged={() => {
          load();
          onMetaChanged?.();
        }}
      />
    </div>
  );
};

export default GuaranteeList;
